import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import type { ChannelMember } from "@/api/connectApi";
import { FormattingToolbar } from "./FormattingToolbar";

interface MentionTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
    members: ChannelMember[];
    /** When true, renders a formatting toolbar above the textarea. Default: true. */
    showFormattingBar?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Special broadcast mentions — rendered as orange/amber pills in the menu
// so users know they'll notify the whole channel.
// ─────────────────────────────────────────────────────────────────────────────
interface SpecialMention {
    kind: "special";
    id: string;
    label: string;
    description: string;
}

interface RegularMention {
    kind: "member";
    member: ChannelMember;
}

type MentionEntry = SpecialMention | RegularMention;

const SPECIAL_MENTIONS: SpecialMention[] = [
    { kind: "special", id: "channel",  label: "channel",  description: "Notify all channel members" },
    { kind: "special", id: "everyone", label: "everyone", description: "Notify everyone in the team" },
];

/**
 * Thin wrapper around a <textarea> that pops a mention menu when the caret is
 * just after an `@query` token. Picking an entry replaces the token with
 * `@<userId>` — that's the same form the server-side parser in
 * `MentionParser` expects.
 *
 * Extras over the original:
 *  - `@channel` and `@everyone` appear as special entries (amber pill)
 *  - Listens for `connect:insert-emoji` CustomEvents from EmojiPicker and
 *    inserts the emoji at the current cursor position.
 *  - Exposes a `FormattingToolbar` above the textarea (opt-out via
 *    `showFormattingBar={false}`).
 */
export const MentionTextarea = forwardRef<HTMLTextAreaElement, MentionTextareaProps>(
    function MentionTextarea(
        { members, onChange, value, showFormattingBar = true, className, ...rest },
        forwardedRef
    ) {
        const ownRef = useRef<HTMLTextAreaElement | null>(null);
        const [menu, setMenu] = useState<{
            query: string;
            tokenStart: number;
            highlight: number;
        } | null>(null);

        const setRefs = (el: HTMLTextAreaElement | null) => {
            ownRef.current = el;
            if (typeof forwardedRef === "function") forwardedRef(el);
            else if (forwardedRef) forwardedRef.current = el;
        };

        // ── Build the combined match list (specials first, then members) ────
        const matches: MentionEntry[] = (() => {
            if (!menu) return [];
            const q = menu.query.toLowerCase();

            const specials: SpecialMention[] = q.length === 0
                ? SPECIAL_MENTIONS
                : SPECIAL_MENTIONS.filter((s) => s.id.startsWith(q) || s.label.startsWith(q));

            const memberMatches: RegularMention[] = (q.length === 0 ? members.slice(0, 6) : members
                .filter((m) =>
                    (m.displayName ?? "").toLowerCase().includes(q) ||
                    m.userEmail.toLowerCase().includes(q) ||
                    m.userId.toLowerCase().includes(q)
                )
                .slice(0, 6))
                .map((m) => ({ kind: "member" as const, member: m }));

            return [...specials.map((s) => ({ ...s } as SpecialMention)), ...memberMatches].slice(0, 8);
        })();

        // Scan the text around the caret for the active @-token.
        const rescan = useCallback((text: string, caret: number) => {
            const start = Math.max(0, caret - 40);
            const slice = text.slice(start, caret);
            const match = /@([A-Za-z0-9._\-]*)$/.exec(slice);
            if (!match) {
                setMenu(null);
                return;
            }
            const tokenStart = start + match.index;
            if (tokenStart > 0) {
                const prev = text[tokenStart - 1];
                if (prev && /[A-Za-z0-9]/.test(prev)) {
                    setMenu(null);
                    return;
                }
            }
            setMenu((prev) => ({
                query: match[1] ?? "",
                tokenStart,
                highlight: prev?.tokenStart === tokenStart ? prev.highlight : 0
            }));
        }, []);

        useEffect(() => {
            setMenu((prev) => (prev ? { ...prev, highlight: 0 } : prev));
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [matches.map((m) => (m.kind === "member" ? m.member.userId : m.id)).join(",")]);

        // ── Commit a regular member mention ─────────────────────────────────
        const commitMember = useCallback((member: ChannelMember) => {
            if (!menu || !ownRef.current) return;
            const el = ownRef.current;
            const text = String(value ?? "");
            const before = text.slice(0, menu.tokenStart);
            const after = text.slice(el.selectionStart ?? text.length);
            const insertion = `@${member.userId} `;
            const next = before + insertion + after;
            const evt = { target: { value: next } } as unknown as React.ChangeEvent<HTMLTextAreaElement>;
            onChange?.(evt);
            setMenu(null);
            requestAnimationFrame(() => {
                el.focus();
                const caret = before.length + insertion.length;
                el.setSelectionRange(caret, caret);
            });
        }, [menu, onChange, value]);

        // ── Commit a special mention (@channel / @everyone) ─────────────────
        const commitSpecial = useCallback((special: SpecialMention) => {
            if (!menu || !ownRef.current) return;
            const el = ownRef.current;
            const text = String(value ?? "");
            const before = text.slice(0, menu.tokenStart);
            const after = text.slice(el.selectionStart ?? text.length);
            const insertion = `@${special.id} `;
            const next = before + insertion + after;
            const evt = { target: { value: next } } as unknown as React.ChangeEvent<HTMLTextAreaElement>;
            onChange?.(evt);
            setMenu(null);
            requestAnimationFrame(() => {
                el.focus();
                const caret = before.length + insertion.length;
                el.setSelectionRange(caret, caret);
            });
        }, [menu, onChange, value]);

        const commit = useCallback((entry: MentionEntry) => {
            if (entry.kind === "member") {
                commitMember(entry.member);
            } else {
                commitSpecial(entry);
            }
        }, [commitMember, commitSpecial]);

        // ── Emoji insertion via CustomEvent ──────────────────────────────────
        useEffect(() => {
            const onEmoji = (e: Event) => {
                const { emoji } = (e as CustomEvent<{ emoji: string }>).detail;
                const el = ownRef.current;
                if (!el) return;
                const start = el.selectionStart ?? el.value.length;
                const end = el.selectionEnd ?? start;
                const text = String(value ?? "");
                const next = text.slice(0, start) + emoji + text.slice(end);
                const evt = { target: { value: next } } as unknown as React.ChangeEvent<HTMLTextAreaElement>;
                onChange?.(evt);
                requestAnimationFrame(() => {
                    el.focus();
                    const caret = start + emoji.length;
                    el.setSelectionRange(caret, caret);
                });
            };
            document.addEventListener("connect:insert-emoji", onEmoji);
            return () => document.removeEventListener("connect:insert-emoji", onEmoji);
        }, [onChange, value]);

        // ── Formatting toolbar handler ────────────────────────────────────────
        const handleFormat = useCallback(
            (prefix: string, suffix: string) => {
                const el = ownRef.current;
                if (!el) return;
                const { selectionStart: start, selectionEnd: end, value: tv } = el;
                const selected = tv.slice(start, end);
                const insertion = prefix + (selected || "text") + suffix;
                const next = tv.slice(0, start) + insertion + tv.slice(end);
                const evt = { target: { value: next } } as unknown as React.ChangeEvent<HTMLTextAreaElement>;
                onChange?.(evt);
                requestAnimationFrame(() => {
                    el.focus();
                    if (selected) {
                        el.setSelectionRange(start + prefix.length, start + prefix.length + selected.length);
                    } else {
                        // Select the placeholder "text" so the user can type over it
                        el.setSelectionRange(start + prefix.length, start + prefix.length + 4);
                    }
                });
            },
            [onChange]
        );

        const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            // Keyboard shortcuts for formatting (Ctrl+B, Ctrl+I, Ctrl+U)
            if (e.ctrlKey || e.metaKey) {
                if (e.key === "b" || e.key === "B") { e.preventDefault(); handleFormat("**", "**"); return; }
                if (e.key === "i" || e.key === "I") { e.preventDefault(); handleFormat("_", "_"); return; }
                if (e.key === "u" || e.key === "U") { e.preventDefault(); handleFormat("<u>", "</u>"); return; }
            }

            if (menu && matches.length > 0) {
                if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setMenu({ ...menu, highlight: (menu.highlight + 1) % matches.length });
                    return;
                }
                if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setMenu({ ...menu, highlight: (menu.highlight - 1 + matches.length) % matches.length });
                    return;
                }
                if (e.key === "Enter" || e.key === "Tab") {
                    e.preventDefault();
                    const pick = matches[menu.highlight] ?? matches[0];
                    if (pick) commit(pick);
                    return;
                }
                if (e.key === "Escape") {
                    e.preventDefault();
                    setMenu(null);
                    return;
                }
            }
            rest.onKeyDown?.(e);
        };

        return (
            <div className="relative flex-1">
                {showFormattingBar ? (
                    <FormattingToolbar
                        textareaRef={ownRef as React.RefObject<HTMLTextAreaElement>}
                        onFormat={handleFormat}
                        className="mb-1"
                    />
                ) : null}
                <textarea
                    {...rest}
                    ref={setRefs}
                    value={value}
                    className={className}
                    onChange={(e) => {
                        onChange?.(e);
                        rescan(e.target.value, e.target.selectionStart ?? e.target.value.length);
                    }}
                    onKeyUp={(e) => {
                        rescan(e.currentTarget.value, e.currentTarget.selectionStart ?? e.currentTarget.value.length);
                    }}
                    onClick={(e) => {
                        rescan(e.currentTarget.value, e.currentTarget.selectionStart ?? e.currentTarget.value.length);
                    }}
                    onKeyDown={onKeyDown}
                />
                {menu && matches.length > 0 ? (
                    <div className="absolute bottom-full left-0 z-20 mb-1 max-h-48 w-72 overflow-auto rounded-md border bg-card p-1 shadow-lg">
                        {matches.map((entry, idx) =>
                            entry.kind === "special" ? (
                                <SpecialMentionRow
                                    key={entry.id}
                                    entry={entry}
                                    highlighted={idx === menu.highlight}
                                    onMouseDown={() => commit(entry)}
                                />
                            ) : (
                                <MemberMentionRow
                                    key={entry.member.id}
                                    member={entry.member}
                                    highlighted={idx === menu.highlight}
                                    onMouseDown={() => commit(entry)}
                                />
                            )
                        )}
                    </div>
                ) : null}
            </div>
        );
    }
);

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

function SpecialMentionRow({
    entry,
    highlighted,
    onMouseDown,
}: {
    entry: SpecialMention;
    highlighted: boolean;
    onMouseDown: () => void;
}) {
    return (
        <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); onMouseDown(); }}
            className={`flex w-full items-center gap-2 rounded-md px-2 py-1 text-left text-sm ${
                highlighted ? "bg-amber-100 dark:bg-amber-900/40" : "hover:bg-muted"
            }`}
        >
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-100 text-[10px] font-bold dark:bg-amber-900/60">
                #
            </span>
            <span className="min-w-0 flex-1">
                <span className="font-medium text-amber-700 dark:text-amber-400">@{entry.label}</span>
                <span className="ml-2 text-[10px] text-muted-foreground">{entry.description}</span>
            </span>
        </button>
    );
}

function MemberMentionRow({
    member,
    highlighted,
    onMouseDown,
}: {
    member: ChannelMember;
    highlighted: boolean;
    onMouseDown: () => void;
}) {
    return (
        <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); onMouseDown(); }}
            className={`flex w-full items-center gap-2 rounded-md px-2 py-1 text-left text-sm ${
                highlighted ? "bg-accent" : "hover:bg-muted"
            }`}
        >
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-semibold">
                {(member.displayName ?? member.userEmail).slice(0, 2).toUpperCase()}
            </span>
            <span className="min-w-0 flex-1 truncate">
                {member.displayName ?? member.userEmail}
            </span>
            <span className="shrink-0 text-[10px] text-muted-foreground">
                @{member.userId}
            </span>
        </button>
    );
}
